import { Tabs, TabsProps } from "antd";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { translate } from "shared/languages";
import { Staff } from "shared/types";
import InstructorClassTypes from "./InstructorClassTypes";
import InstructorWorkingHours from "./InstructorWorkingHours";
import TimeOffPage from "./TimeOffPage";

interface InstructorManagementTabProps {
  staffId: string;
  staff: Staff;
}

// The "Manajemen" tab's own sub-tabs — grouped under one parent instead of
// three flat top-level tabs, so "Kelas" (class history) and "Kelas yang
// Ditugaskan" (assigned classes) don't sit side by side in the same bar. Each
// sub-tab is independently gated by its own business-settings sub-flag (see
// EmployeeManagementPage.tsx) — a studio can, say, use only class assignment
// without tracking working hours or leave. Uncontrolled (no URL deep-link for
// which sub-tab): this is one level below InstructorDetails' own ?tab= param,
// and there's no real need to bookmark a specific sub-tab.
const InstructorManagementTab = ({ staffId, staff }: InstructorManagementTabProps) => {
  const { data: settings } = useBusinessSettings();

  const items: TabsProps["items"] = [
    ...(settings?.employee_management_working_hours_enabled
      ? [
          {
            key: "hours",
            label: translate("tabs.working_hours"),
            children: (
              <InstructorWorkingHours
                staffId={staffId}
                hours={staff.working_hour}
                workingHoursConfigured={staff.working_hours_configured}
                archivedWorkingHours={staff.archived_working_hours}
              />
            ),
          },
        ]
      : []),
    ...(settings?.employee_management_leave_enabled
      ? [
          {
            key: "leave",
            label: translate("tabs.time_off"),
            children: <TimeOffPage staffId={staffId} leaves={staff.leave} />,
          },
        ]
      : []),
    ...(settings?.employee_management_class_assignment_enabled
      ? [
          {
            key: "classes",
            label: translate("tabs.assigned_classes"),
            children: (
              <InstructorClassTypes
                staffId={staffId}
                classTypes={staff.class_types}
                assignedAllClassTypes={staff.assigned_all_class_types}
                archivedClassAssignment={staff.archived_class_assignment}
              />
            ),
          },
        ]
      : []),
  ];

  return <Tabs defaultActiveKey={items[0]?.key} size="small" items={items} />;
};

export default InstructorManagementTab;
